import { Link, useNavigate } from '@tanstack/react-router';
import type { AdminEventListItemDto, CityDto } from '@upme/contracts';
import { Badge } from '@upme/ui/components/badge';
import { Button } from '@upme/ui/components/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@upme/ui/components/table';
import type { EventsSearch } from '../../../routes/_authenticated/events/route';
import { EmptyState } from '../../../shared/components/empty-state';
import { formatEventDateTime, STATUS_LABEL, STATUS_VARIANT } from '../lib/format';

interface Props {
  events: ReadonlyArray<AdminEventListItemDto>;
  cities: ReadonlyArray<CityDto>;
  nextCursor: string | null;
  currentCursor?: string | undefined;
}

export function EventsTable({ events, cities, nextCursor, currentCursor }: Props) {
  const navigate = useNavigate();
  const cityById = new Map(cities.map((c) => [c.id, c]));

  if (events.length === 0 && !currentCursor) {
    return (
      <EmptyState
        title="Событий пока нет"
        description="Создайте первое событие — оно появится в каталоге после публикации."
        cta={
          <Button asChild>
            <Link to="/events/new">Новое событие</Link>
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Название</TableHead>
            <TableHead>Город</TableHead>
            <TableHead>Начало</TableHead>
            <TableHead>Категории</TableHead>
            <TableHead>RSVP</TableHead>
            <TableHead>Статус</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((e) => {
            const city = cityById.get(e.cityId);
            const tz = city?.timezone ?? 'UTC';
            return (
              <TableRow key={e.id}>
                <TableCell>
                  <Link to="/events/$eventId" params={{ eventId: e.id }} className="hover:underline">
                    {e.title}
                  </Link>
                  <div className="text-xs text-muted-foreground truncate max-w-[280px]">{e.venueText}</div>
                </TableCell>
                <TableCell>{city?.name ?? e.cityId.slice(0, 8)}</TableCell>
                <TableCell className="tabular-nums">
                  {formatEventDateTime(e.startsAt, tz, e.hasStartTime)}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {e.categories.map((c) => (
                      <Badge key={c.id} variant="outline">
                        {c.icon ? `${c.icon} ` : ''}
                        {c.name}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>{e.attendanceCount}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    <Badge variant={STATUS_VARIANT[e.status]}>{STATUS_LABEL[e.status]}</Badge>
                    {/* У опубликованного/архивного события previouslyPublished истинно всегда
                        и ничего не сообщает — бейдж имеет смысл только на модерации. */}
                    {e.previouslyPublished && e.status === 'PENDING_MODERATION' && (
                      <Badge variant="outline">Повторная</Badge>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {nextCursor && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() =>
              navigate({
                to: '/events',
                // TanStack types `prev` as the full union of all routes' search
                // schemas — including ones whose `status` enum disjoints with
                // ours. Cast to the events-local shape: TanStack still strips
                // unknown keys at runtime, and `validateSearch` re-narrows the URL.
                search: (prev) => ({ ...(prev as EventsSearch), cursor: nextCursor }),
              })
            }
          >
            Загрузить ещё
          </Button>
        </div>
      )}
    </div>
  );
}
